/**
 * NotFoundPage — the router's real 404 (#626, nav honesty).
 *
 * Before this page existed, App.tsx's `path="*"` catch-all re-rendered
 * RootRoute, so a mistyped or stale URL silently showed the dashboard or
 * landing page as if nothing were wrong. Now unknown URLs say so, in the
 * editorial voice: mono eyebrow, Fraunces title with one emphasized word,
 * one-sentence lede, a single action.
 *
 * "Back to home" is role-aware without knowing any roles: it links to "/",
 * which RootRoute resolves — authenticated users bounce to homeFor(role),
 * visitors get the landing page.
 *
 * Mounted OUTSIDE MainLayout (it is the top-level catch-all), so it paints
 * its own full-height surface rather than assuming app-shell chrome — which
 * means it must carry the two pieces of language chrome the shells carry
 * (#2109 rulings brief B23): the machine-translation banner, because every
 * screen in a non-approved language says so, and the switcher, because a
 * page with no way to change language strands the visitor on a stale
 * `?lang=`.
 */
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { PageHead, Emphasis } from '@/components/editorial';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { MachineTranslationBanner } from '@/components/layout/MachineTranslationBanner';
import { NAV_KEY } from '@/i18n/keys/nav';

export function NotFoundPage() {
  // The first page on `eyebrowKey` (#2109 L6): the eyebrow, headline, lede
  // and action all render in the active locale from `shell.json`.
  const { t } = useTranslation('shell');
  return (
    <div className="flex h-full min-h-[60vh] flex-col bg-stevens-bg">
      {/* The same row MainLayout carries: the banner fills, the switcher sits at the end. */}
      <div className="flex items-center justify-end gap-3 px-4 pt-1 md:px-6 lg:px-8">
        <MachineTranslationBanner className="min-w-0 flex-1 rounded-md border" />
        <LanguageSwitcher />
      </div>
      <main className="flex flex-1 items-center justify-center overflow-y-auto px-6 py-16">
        <div className="w-full max-w-md">
          <PageHead
            eyebrowKey={NAV_KEY.notFound}
            title={
              <Trans t={t} i18nKey="notFound.title" components={{ em: <Emphasis>{''}</Emphasis> }} />
            }
            lede={t('notFound.lede')}
          />
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-transparent bg-stevens-indigo-solid px-[18px] py-[11px] text-body font-medium text-stevens-indigo-solid-fg transition-[background,transform,box-shadow,border-color] duration-150 hover:bg-stevens-indigo-solid-2 active:scale-[0.98]"
          >
            {t('notFound.backHome')}
          </Link>
        </div>
      </main>
    </div>
  );
}
